// Desktop-only control: persist the sidebar expanded (skip hover close),
// plus a chevron that hides Location Links + global tools so the mode list can breathe.
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

export const SIDEBAR_KEEP_OPEN_STORAGE_KEY = "shell.sidebar.keepOpen";
export const SIDEBAR_FOOTER_OPEN_STORAGE_KEY = "shell.sidebar.footerOpen";
export const SIDEBAR_FOOTER_DOM_ID = "sidebar-desktop-footer";

export function useSidebarKeepOpen(): [boolean, (next: boolean) => void] {
  const [keepOpen, setKeepOpen] = useLocalStorageState<boolean>(
    SIDEBAR_KEEP_OPEN_STORAGE_KEY,
    false,
  );
  return [keepOpen, setKeepOpen];
}

export function useSidebarFooterOpen(): [boolean, (next: boolean) => void] {
  const [footerOpen, setFooterOpen] = useLocalStorageState<boolean>(
    SIDEBAR_FOOTER_OPEN_STORAGE_KEY,
    true,
  );
  return [footerOpen, setFooterOpen];
}

export function SidebarKeepOpenControl({
  expanded,
  keepOpen,
  onKeepOpenChange,
  footerOpen,
  onFooterOpenChange,
}: {
  expanded: boolean;
  keepOpen: boolean;
  onKeepOpenChange: (next: boolean) => void;
  footerOpen: boolean;
  onFooterOpenChange: (next: boolean) => void;
}) {
  const Chevron = footerOpen ? ChevronDown : ChevronUp;
  return (
    <div
      className={
        "mx-2 mb-1 flex items-center gap-0.5 border-t border-gray-200 pt-1 dark:border-zinc-800 " +
        (expanded ? "" : "justify-center")
      }
    >
      <label
        className={
          "flex min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs " +
          "text-gray-600 hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800/80 " +
          (expanded ? "" : "px-1")
        }
        title="Keep sidebar open"
      >
        <input
          type="checkbox"
          className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-900"
          checked={keepOpen}
          onChange={(e) => onKeepOpenChange(e.target.checked)}
          aria-label="Keep sidebar open"
        />
        {expanded ? <span className="truncate">Keep sidebar open</span> : null}
      </label>
      <button
        type="button"
        aria-expanded={footerOpen}
        aria-controls={SIDEBAR_FOOTER_DOM_ID}
        aria-label={footerOpen ? "Hide sidebar tools" : "Show sidebar tools"}
        title={footerOpen ? "Hide location links and tools" : "Show location links and tools"}
        onClick={() => onFooterOpenChange(!footerOpen)}
        className={
          "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md " +
          "text-gray-500 hover:bg-gray-100 hover:text-gray-800 " +
          "dark:text-zinc-400 dark:hover:bg-zinc-800/80 dark:hover:text-zinc-100"
        }
      >
        <Chevron className="h-4 w-4" strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}
